import React, { useState, useEffect } from "react";
import { Table, Button, Input, Tabs, Row, Col, Select, Modal, Space, Typography } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import { fetchMedicalHistories, deleteMedicalHistory, updateMedicalHistory, createMedicalHistory } from "../../../../api/healthcare/PatientApi";
import { detailFieldsMapping } from "../../../../types/healthcare/MedicalHistoryTypes";
import CreateMedicalHistoryModal from "./submodals/CreateMedicalHistoryModal";
import { Patient } from "../../../../types/healthcare/PatientTypes";
import { MedicalHistory } from "../../../../types/healthcare/MedicalHistoryTypes";
import { useNotifications } from "../../../../context/NotificationContext";
import SectionCard from "../../../../components/shared/SectionCard";
import StatusTag from "../../../../components/shared/StatusTag";

const { Text } = Typography;

const categoryToHistoryTypes: Record<string, string[]> = {
	medical_conditions: ["diagnosis", "chronic_condition", "acute_condition", "infectious_disease"],
	surgical_procedures: ["surgical_procedure", "medical_procedure", "implant"],
	allergies: ["allergy"],
	family_genetic: ["family_history", "genetic_predisposition"],
	preventive_care: ["immunization", "screening_test", "preventive_measure"],
	lifestyle_social: ["lifestyle", "substance_use", "environmental_exposure", "occupational_hazard", "travel_history"],
	mental_health: ["mental_health", "psychiatric_treatment", "cognitive_assessment"],
	reproductive_health: ["pregnancy", "obstetric_history", "gynecological_condition", "contraception"],
	pediatric: ["developmental_milestone", "congenital_condition", "growth_assessment"],
	hospitalizations_er: ["hospitalization", "emergency_visit"],
	functional_assessments: ["functional_assessment", "nutritional_assessment", "sleep_assessment", "pain_assessment", "rehabilitation"],
};

const historyTypeCategories = [
	{ label: "Medical Conditions", value: "medical_conditions" },
	{ label: "Surgical & Procedures", value: "surgical_procedures" },
	{ label: "Allergies", value: "allergies" },
	{ label: "Family & Genetic", value: "family_genetic" },
	{ label: "Preventive Care", value: "preventive_care" },
	{ label: "Lifestyle & Social", value: "lifestyle_social" },
	{ label: "Mental Health", value: "mental_health" },
	{ label: "Reproductive Health", value: "reproductive_health" },
	{ label: "Pediatric", value: "pediatric" },
	{ label: "Hospitalizations & ER", value: "hospitalizations_er" },
	{ label: "Functional Assessments", value: "functional_assessments" },
];

interface MedicalHistoryTabProps {
	patient: Patient;
	handleAutoSave?: () => void;
}

const MedicalHistoryTab: React.FC<MedicalHistoryTabProps> = ({ patient }) => {
	const [histories, setHistories] = useState<MedicalHistory[]>([]);
	const [activeHistories, setActiveHistories] = useState<MedicalHistory[]>([]);
	const [inactiveHistories, setInactiveHistories] = useState<MedicalHistory[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [dialogVisible, setDialogVisible] = useState<boolean>(false);
	const [currentRecord, setCurrentRecord] = useState<Partial<MedicalHistory> | null>(null);
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [activeTab, setActiveTab] = useState<string>("all");
	const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
	const { showError, showSuccess } = useNotifications();

	useEffect(() => {
		if (patient?.id) loadHistories();
	}, [patient]);

	const loadHistories = async () => {
		setLoading(true);
		try {
			const data = await fetchMedicalHistories(patient.id);
			setHistories(data);
			const active = data.filter((h) => {
				const d = typeof h.details === "string" ? JSON.parse(h.details) : h.details;
				return d?.status === "active" || d?.status === "chronic";
			});
			const inactive = data.filter((h) => {
				const d = typeof h.details === "string" ? JSON.parse(h.details) : h.details;
				return d?.status !== "active" && d?.status !== "chronic";
			});
			setActiveHistories(active);
			setInactiveHistories(inactive);
		} catch {
			showError("Failed to load medical histories");
		} finally {
			setLoading(false);
		}
	};

	const openNewRecordDialog = () => {
		setCurrentRecord({ patient_id: patient.id, history_type: "", description: "", details: {} });
		setIsEditing(false);
		setDialogVisible(true);
	};

	const handleDelete = async (record: MedicalHistory) => {
		Modal.confirm({
			title: "Confirm Deletion",
			content: "Are you sure you want to delete this medical history record?",
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					await deleteMedicalHistory(record.id);
					showSuccess("Record deleted successfully");
					loadHistories();
				} catch {
					showError("Failed to delete record");
				}
			},
		});
	};

	const getFilteredHistories = (list: MedicalHistory[]) => {
		let filtered = [...list];
		if (searchTerm) {
			const q = searchTerm.toLowerCase();
			filtered = filtered.filter((h) => h.history_type?.toLowerCase().includes(q) || h.description?.toLowerCase().includes(q));
		}
		if (selectedCategory) {
			const types = categoryToHistoryTypes[selectedCategory] || [];
			filtered = filtered.filter((h) => types.includes(h.history_type));
		}
		return filtered;
	};


	const saveRecord = async () => {
		if (!currentRecord) return;
		if (!currentRecord.history_type) {
			showError("History type is required");
			return;
		}
		try {
			const payload = { ...currentRecord, details: JSON.stringify(currentRecord.details || {}) } as unknown as Partial<MedicalHistory>;
			if (isEditing && currentRecord?.id) {
				await updateMedicalHistory(currentRecord.id, payload);
				showSuccess("Record updated successfully");
			} else {
				await createMedicalHistory(payload);
				showSuccess("Record created successfully");
			}
			loadHistories();
			setDialogVisible(false);
		} catch {
			showError("Failed to save record");
		}
	};

	const expandedRowRender = (record: MedicalHistory) => {
		if (!record.details) return <Text type="secondary">No details available.</Text>;
		const detailsObj = typeof record.details === "string" ? JSON.parse(record.details) : record.details;
		const fields = detailFieldsMapping[record.history_type as keyof typeof detailFieldsMapping];
		if (!fields) return <Text type="secondary">No field definitions found for this history type.</Text>;
		return (
			<div style={{ padding: "12px 0" }}>
				<Row gutter={[16, 16]}>
					<Col span={12}>
						<Text strong style={{ display: "block", marginBottom: 8 }}>
							Medical History Details
						</Text>
						<Row gutter={[16, 8]}>
							{fields.map((field) => {
								const value = detailsObj[field.name];
								if (value === undefined || value === null || value === "" || field.label === "Additional Notes") return null;
								let displayValue: React.ReactNode = value;
								if (field.type === "date" && value) displayValue = new Date(value).toLocaleDateString();
								else if (field.type === "boolean") displayValue = value ? "Yes" : "No";
								else if (field.type === "dropdown" && field.name === "severity") displayValue = <StatusTag status={value} />;
								else if (field.type === "dropdown" && field.name === "status") displayValue = <StatusTag status={value} />;
								return (
									<Col span={12} key={field.name}>
										<Text strong>{field.label}:</Text> <Text>{displayValue}</Text>
									</Col>
								);
							})}
						</Row>
					</Col>
					<Col span={12}>
						<Text strong style={{ display: "block", marginBottom: 8 }}>
							Additional Notes
						</Text>
						{detailsObj.notes ? <Text style={{ lineHeight: 1.6 }}>{detailsObj.notes}</Text> : <Text type="secondary">No additional notes provided.</Text>}
					</Col>
				</Row>
			</div>
		);
	};

	const columns = [
		{
			title: "Type",
			dataIndex: "history_type",
			key: "history_type",
			sorter: (a: MedicalHistory, b: MedicalHistory) => (a.history_type || "").localeCompare(b.history_type || ""),
			render: (text: string) => <Text style={{ textTransform: "capitalize" }}>{text.replace(/_/g, " ")}</Text>,
		},
		{ title: "Description", dataIndex: "description", key: "description" },
		{ title: "Date Identified", dataIndex: "date_identified", key: "date_identified", render: (date: string) => (date ? new Date(date).toLocaleDateString() : "—") },
		{
			title: "Status",
			key: "status",
			render: (record: MedicalHistory) => {
				const d = typeof record.details === "string" ? JSON.parse(record.details) : record.details;
				return d?.status ? <StatusTag status={d.status} /> : null;
			},
		},
		{
			title: "Severity",
			key: "severity",
			render: (record: MedicalHistory) => {
				const d = typeof record.details === "string" ? JSON.parse(record.details) : record.details;
				return d?.severity ? <StatusTag status={d.severity} /> : null;
			},
		},
		{
			title: "Actions",
			key: "actions",
			width: 120,
			fixed: "right" as const,
			render: (record: MedicalHistory) => (
				<Space>
					<Button
						type="text"
						size="small"
						icon={<EditOutlined />}
						onClick={() => {
							setCurrentRecord(record);
							setIsEditing(true);
							setDialogVisible(true);
						}}
					/>
					<Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
				</Space>
			),
		},
	];

	const tableProps = {
		columns,
		loading,
		size: "small" as const,
		scroll: { x: "max-content" as const },
		expandable: { expandedRowRender, expandedRowKeys, onExpandedRowsChange: (keys: React.Key[]) => setExpandedRowKeys([...keys]) },
		pagination: { pageSize: 10, showSizeChanger: true, showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} of ${total} records` },
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
				<Button type="primary" icon={<PlusOutlined />} onClick={openNewRecordDialog}>
					Add Medical History
				</Button>
				<Space>
					<Input placeholder="Search medical history" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} prefix={<SearchOutlined />} style={{ width: 250 }} allowClear />
					<Select style={{ width: 200 }} value={selectedCategory} onChange={setSelectedCategory} placeholder="Filter by category" options={historyTypeCategories} allowClear />
				</Space>
			</div>

			<>
				<Tabs
					activeKey={activeTab}
					onChange={setActiveTab}
					items={[
						{ key: "all", label: "All Records", children: <Table rowKey="id" dataSource={getFilteredHistories(histories)} {...tableProps} /> },
						{ key: "active", label: "Active", children: <Table rowKey="id" dataSource={getFilteredHistories(activeHistories)} {...tableProps} /> },
						{ key: "inactive", label: "Resolved/Inactive", children: <Table rowKey="id" dataSource={getFilteredHistories(inactiveHistories)} {...tableProps} /> },
						{
							key: "category",
							label: "By Category",
							children: (
								<>
									<div style={{ padding: "12px 16px" }}>
										<Select style={{ width: 300, marginBottom: 12 }} value={selectedCategory} onChange={setSelectedCategory} placeholder="Select Category" options={historyTypeCategories} allowClear />
									</div>
									<Table rowKey="id" dataSource={selectedCategory ? getFilteredHistories(histories) : []} {...tableProps} locale={{ emptyText: selectedCategory ? "No records found for this category" : "Please select a category" }} />
								</>
							),
						},
						{
							key: "critical",
							label: "Critical Conditions",
							children: <Table rowKey="id" dataSource={histories.filter((h) => { const d = typeof h.details === "string" ? JSON.parse(h.details) : h.details; return d?.severity === "severe" || d?.severity === "life_threatening"; })} {...tableProps} />,
						},
						{
							key: "chronic",
							label: "Chronic Conditions",
							children: <Table rowKey="id" dataSource={histories.filter((h) => { const d = typeof h.details === "string" ? JSON.parse(h.details) : h.details; return d?.status === "chronic"; })} {...tableProps} />,
						},
					]}
				/>
			</>

			{dialogVisible && currentRecord && (
				<Modal
					title={isEditing ? "Edit Medical History" : "Create Medical History"}
					open={dialogVisible}
					onCancel={() => setDialogVisible(false)}
					width="85vw"
					footer={[
						<Button key="cancel" onClick={() => setDialogVisible(false)}>
							Cancel
						</Button>,
						<Button key="submit" type="primary" onClick={saveRecord}>
							{isEditing ? "Update" : "Save"}
						</Button>,
					]}
					destroyOnClose
				>
					<CreateMedicalHistoryModal currentRecord={currentRecord} isEditing={isEditing} setCurrentRecord={setCurrentRecord} />
				</Modal>
			)}
		</div>
	);
};

export default MedicalHistoryTab;
